import { useState, useEffect } from "react";
import ProjectInfoService, {
  ProjectInfoFields,
} from "../services/ProjectInfoService";
import ProfileService from "../services/ProfileService";

/**
 * Custom hook for managing energy consumption scenarios
 *
 * @param {string} projectId - Project ID from URL parameters
 * @returns {Object} State and functions for energy consumption scenarios management
 */
const useEnergyConsumptionScenarios = (projectId) => {
  // State management
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedScenarios, setSelectedScenarios] = useState([]);

  const token = localStorage.getItem("token");
  const apiRoot = process.env.REACT_APP_API_ROOT;
  const apiRootOperational = process.env.REACT_APP_API_ROOT_OPERATIONAL;

  const profileService = new ProfileService(apiRoot, token);
  const projectInfoService = new ProjectInfoService(apiRoot, token);

  /**
   * Fetch scenarios from the server
   */
  const fetchScenarios = async () => {
    if (!projectId) {
      setError("Project ID is missing");
      setLoading(false);
      return { success: false, error: "Project ID is missing" };
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${apiRoot}/api/project_info/?project_id=${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch scenarios");
      }

      const data = await response.json();
      let scenarioList = data[0]?.room_level_scenarios;
      if (typeof scenarioList === "string") {
        scenarioList = JSON.parse(scenarioList);
      }
      setScenarios(Array.isArray(scenarioList) ? scenarioList : []);
    } catch (err) {
      console.error("Error fetching scenarios:", err);
      setError(
        "Failed to load energy consumption scenarios. Please try again."
      );
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }

    return { success: true };
  };

  useEffect(() => {
    fetchScenarios();
  }, [projectId]);

  /**
   * Handle scenario selection
   *
   * @param {string} scenarioId - ID of the scenario to select/deselect
   */
  const handleSelectScenario = (scenarioId) => {
    setSelectedScenarios((prev) => {
      if (prev.includes(scenarioId)) {
        return prev.filter((id) => id !== scenarioId);
      } else {
        return [...prev, scenarioId];
      }
    });
  };

  /**
   * Handle selection of all scenarios
   *
   * @param {Array} newSelectedScenarios - Array of scenario IDs to select
   */
  const handleSelectAllScenarios = (newSelectedScenarios) => {
    setSelectedScenarios(newSelectedScenarios);
  };

  /**
   * Delete selected scenarios
   *
   * @returns {Object} Result of the operation
   */
  const deleteScenarios = async () => {
    if (selectedScenarios.length === 0) {
      return { success: false, message: "No scenarios selected" };
    }

    try {
      setIsProcessing(true);

      for (const scenario of selectedScenarios) {
        const response = await fetch(
          `${apiRootOperational}/api/transient_response/scenario?project_id=${projectId}&scenario=${scenario}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to delete scenario");
        }
      }

      // Update project info
      const currentInfo = await projectInfoService.getProjectInfo(projectId);
      let roomLevelScenarios = projectInfoService.parseJsonField(
        currentInfo.room_level_scenarios
      );

      roomLevelScenarios = roomLevelScenarios.filter(
        (s) => !selectedScenarios.includes(s)
      );

      await projectInfoService.updateProjectInfo(projectId, {
        [ProjectInfoFields.ROOM_LEVEL_SCENARIOS]:
          projectInfoService.stringifyJsonField(roomLevelScenarios),
      });

      setScenarios(roomLevelScenarios);
      setSelectedScenarios([]);

      return {
        success: true,
        message: `Selected scenarios deleted successfully.`,
      };
    } catch (err) {
      console.error("Error deleting scenarios:", err);
      return {
        success: false,
        message: err.message || "Failed to delete scenarios",
      };
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Upload a new scenario
   *
   * @param {string} scenarioName - Name of the new scenario
   * @param {File} file - Excel file with scenario data
   * @returns {Object} Result of the operation
   */
  const uploadScenario = async (scenarioName, file) => {
    if (!scenarioName || !file) {
      return {
        success: false,
        message: "Please enter a scenario name and select a file.",
      };
    }

    try {
      setIsProcessing(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("projectId", projectId);
      formData.append("scenario", scenarioName);

      const response = await fetch(
        `${apiRootOperational}/api/upload_dashboard_excel_file`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to upload scenario");
      }

      const data = await response.json();

      await Promise.all([
        projectInfoService.updateMetrics(projectId, [
          {
            level: "building",
            dataKey: "consumption",
          },
        ]),
        projectInfoService.updateListField(
          projectId,
          "room_level_scenarios",
          scenarioName,
          { overwrite: false, unique: true }
        ),
        profileService.updateEnergyCO2(projectId, true),
      ]);

      setScenarios((prev) => [...new Set([...prev, scenarioName])]);

      return {
        success: true,
        message: `Scenario "${scenarioName}" uploaded successfully. ${
          data.records ? `${data.records} records processed.` : ""
        }`,
      };
    } catch (err) {
      console.error("Error in scenario upload process:", err);
      return {
        success: false,
        message: err.message || "Error uploading scenario",
      };
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    scenarios,
    loading,
    isProcessing,
    error,
    selectedScenarios,

    fetchScenarios,
    handleSelectScenario,
    handleSelectAllScenarios,
    deleteScenarios,
    uploadScenario,
  };
};

export default useEnergyConsumptionScenarios;
